import React from 'react';
import { fmtDate as _fmtDate } from '@/lib/dates';
import { StatusBadge, TypeBadge, SubmissionBadge } from './StatusBadge';
import { Card, CardContent } from '@/components/ui/card';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import { Inbox, ChevronRight, ArrowUpDown } from 'lucide-react';
import { MobileRecordCard, MobileRecordList } from '@/components/common/mobile/MobileRecordCard';

const fmtDate = (d) => (d ? (_fmtDate(d) || '—') : '—');

const th = 'sticky top-0 z-10 bg-slate-50 text-[11px] font-semibold uppercase tracking-[0.12em] text-slate-400';

const SortTH = ({ label, col, sortKey, sortDir, onSort, extra = '' }) => {
  const active = sortKey === col;
  return (
    <TableHead className={`${th} py-3.5 ${extra}`}>
      {onSort ? (
        <button type="button" onClick={() => onSort(col)} data-testid={`sr-sort-${col}`}
          className={`inline-flex items-center gap-1 uppercase tracking-[0.12em] ${active ? 'text-[#0E2A47]' : 'hover:text-slate-600'}`}>
          {label}
          <ArrowUpDown size={12} className={active ? 'text-[#8FC93A]' : 'text-slate-300'} />
          {active && <span className="text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
        </button>
      ) : label}
    </TableHead>
  );
};

const ServiceRequestTable = ({ requests, onSelect, emptyLabel = 'No service requests yet.', sortKey, sortDir, onSort, selectable = false, selected, onToggle, onToggleAll }) => {
  if (!requests || requests.length === 0) {
    return (
      <Card className="border-slate-200 shadow-sm rounded-2xl">
        <CardContent className="py-20 text-center">
          <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-slate-50 text-slate-300">
            <Inbox size={28} />
          </div>
          <p className="text-sm text-slate-400">{emptyLabel}</p>
        </CardContent>
      </Card>
    );
  }
  return (
    <>
    <Card className="hidden lg:block border-slate-200 shadow-sm rounded-2xl overflow-hidden">
      <CardContent className="p-0">
        <Table data-testid="service-request-table" containerClassName="max-h-[70vh] overflow-auto">
          <TableHeader>
            <TableRow className="bg-slate-50 hover:bg-slate-50 border-slate-100">
              {selectable && <TableHead className={`${th} py-3.5 pl-6 w-10`}><input type="checkbox" data-testid="sr-select-all" checked={requests.length > 0 && requests.every((r) => selected?.has(r.id))} onChange={(e) => onToggleAll?.(e.target.checked)} /></TableHead>}
              <SortTH label="Request #" col="request_number" sortKey={sortKey} sortDir={sortDir} onSort={onSort} extra={selectable ? '' : 'pl-6'} />
              <SortTH label="Title" col="title" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortTH label="Customer / Location" col="customer_name" sortKey={sortKey} sortDir={sortDir} onSort={onSort} extra="hidden md:table-cell" />
              <SortTH label="Urgency" col="request_type" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <TableHead className={`${th} py-3.5 hidden lg:table-cell`}>Origin</TableHead>
              <SortTH label="Status" col="status" sortKey={sortKey} sortDir={sortDir} onSort={onSort} />
              <SortTH label="Submitted" col="submitted_at" sortKey={sortKey} sortDir={sortDir} onSort={onSort} extra="hidden sm:table-cell" />
              <TableHead className={`${th} py-3.5 pr-6`} />
            </TableRow>
          </TableHeader>
          <TableBody>
            {requests.map((r) => (
              <TableRow key={r.id} className="group cursor-pointer border-slate-100 transition-colors hover:bg-[#8FC93A]/[0.06]"
                data-testid={`sr-row-${r.request_number}`} onClick={() => onSelect(r.id)}>
                {selectable && <TableCell className="py-4 pl-6 w-10" onClick={(e) => e.stopPropagation()}><input type="checkbox" data-testid={`sr-select-${r.request_number}`} checked={selected?.has(r.id) || false} onChange={() => onToggle?.(r.id)} /></TableCell>}
                <TableCell className={`py-4 ${selectable ? '' : 'pl-6'} font-mono text-xs font-bold text-[#0E2A47]`}>{r.request_number}</TableCell>
                <TableCell className="py-4 font-semibold text-slate-800 max-w-[240px] truncate">{r.title}</TableCell>
                <TableCell className="py-4 hidden md:table-cell text-sm">
                  <div className="truncate max-w-[220px] font-medium text-slate-600">{r.customer_name}</div>
                  <div className="text-xs text-slate-400 truncate max-w-[220px]">{r.location_name}</div>
                </TableCell>
                <TableCell className="py-4"><TypeBadge type={r.request_type} /></TableCell>
                <TableCell className="py-4 hidden lg:table-cell"><SubmissionBadge source={r.submission_source} /></TableCell>
                <TableCell className="py-4"><StatusBadge status={r.status} /></TableCell>
                <TableCell className="py-4 hidden sm:table-cell text-sm text-slate-500">{fmtDate(r.submitted_at)}</TableCell>
                <TableCell className="py-4 pr-6 text-right">
                  <ChevronRight size={16} className="inline text-slate-300 transition-colors group-hover:text-[#0E2A47]" />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </CardContent>
    </Card>

    {/* Mobile / tablet: compact request cards — all columns reorganised, tappable */}
    <div className="lg:hidden">
      {onSort && (
        <div className="mb-3 flex flex-wrap gap-2" data-testid="sr-mobile-sort">
          {[
            { col: 'request_number', label: 'Request #' },
            { col: 'title', label: 'Title' },
            { col: 'status', label: 'Status' },
            { col: 'submitted_at', label: 'Submitted' },
          ].map((o) => {
            const active = sortKey === o.col;
            return (
              <button key={o.col} type="button" onClick={() => onSort(o.col)} data-testid={`sr-msort-${o.col}`}
                className={`inline-flex items-center gap-1 rounded-full border px-3 py-1.5 text-xs font-semibold ${active ? 'border-[#0E2A47] bg-[#0E2A47] text-white' : 'border-slate-200 bg-white text-slate-600'}`}>
                {o.label}
                {active && <span className="text-[9px]">{sortDir === 'asc' ? '▲' : '▼'}</span>}
                {!active && <ArrowUpDown size={11} className="text-slate-300" />}
              </button>
            );
          })}
        </div>
      )}
      <MobileRecordList testid="service-request-mobile-list">
        {requests.map((r) => (
          <MobileRecordCard
            key={r.id}
            testid={`sr-card-${r.request_number}`}
            onClick={() => onSelect(r.id)}
            image={selectable ? (
              <div onClick={(e) => e.stopPropagation()} className="pt-0.5">
                <input type="checkbox" data-testid={`sr-card-select-${r.request_number}`} checked={selected?.has(r.id) || false} onChange={() => onToggle?.(r.id)} />
              </div>
            ) : null}
            title={r.request_number}
            badge={<StatusBadge status={r.status} />}
            meta={[
              { label: 'Title', value: r.title, full: true },
              { label: 'Customer', value: r.customer_name },
              { label: 'Location', value: r.location_name },
              { label: 'Urgency', value: <TypeBadge type={r.request_type} /> },
              { label: 'Origin', value: <SubmissionBadge source={r.submission_source} /> },
              { label: 'Submitted', value: fmtDate(r.submitted_at), full: true },
            ]}
          />
        ))}
      </MobileRecordList>
    </div>
    </>
  );
};

export default ServiceRequestTable;
